import { useEffect, useMemo, useRef, useState, type ElementType, type ReactNode } from "react";
import DOMPurify from "dompurify";
import ReactMarkdown, { defaultUrlTransform, type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { AgentFile } from "../types/api";
import { HtmlPreviewFrame } from "./HtmlPreviewFrame";
import { inlineLocalImages } from "../lib/htmlImages";
import { stripCitationMarkers } from "../lib/citationMarkers";
import { PdfPreview } from "./PdfPreview";
import {
  buildSandboxedHtmlDocument,
  extractMarkdownHeadings,
  filePreviewKind,
  previewSizeAllowed,
  safeMarkdownImageSource,
  videoMimeType,
  type FilePreviewKind,
  type MarkdownHeading
} from "../lib/filePreview";

interface PreviewLabels {
  close: string;
  download: string;
  htmlSafety: string;
  htmlLive: string;
  imageTooLarge: (name: string) => string;
  imageUnavailable: (name: string) => string;
  kind: Record<FilePreviewKind, string>;
  loading: string;
  outline: string;
  pdfPage: (page: number, total: number) => string;
  pdfPrev: string;
  pdfNext: string;
  remoteImageBlocked: string;
  retry: string;
  tooLarge: string;
  unavailable: string;
  unsupported: string;
}

interface Props {
  file: AgentFile | null;
  fetchFile: (file: AgentFile) => Promise<Blob>;
  downloadFile: (file: AgentFile) => Promise<void>;
  labels: PreviewLabels;
  onClose: () => void;
}

type PreviewState =
  | { status: "idle" | "loading" | "unsupported" }
  | { status: "markdown"; text: string }
  | { status: "html"; document: string; raw: string }
  | { status: "image" | "video"; url: string }
  | { status: "pdf"; data: ArrayBuffer }
  | { status: "skipped" | "error"; message: string };

const STATIC_HTML_FALLBACK_LIMIT = 2 * 1024 * 1024;

const HTML_FORBID_TAGS = [
  "script", "iframe", "frame", "frameset", "object", "embed", "applet",
  "form", "input", "button", "textarea", "select", "option", "meta", "link", "base"
];
const HTML_FORBID_ATTR = [
  "href", "src", "srcset", "poster", "action", "formaction", "srcdoc", "xlink:href"
];

function headingComponent(tag: ElementType, headingsByLine: Map<number, string>) {
  return function Heading({ node, children, ...props }: { node?: { position?: { start?: { line?: number } } }; children?: ReactNode }) {
    const line = node?.position?.start?.line;
    const id = line ? headingsByLine.get(line) : undefined;
    const Tag = tag;
    return <Tag id={id} {...props}>{children}</Tag>;
  };
}

export function filePreviewUrlTransform(url: string, _key: string, node: { tagName?: string }): string {
  // Preserve image URLs only long enough for our custom img renderer to apply
  // the stricter no-network allowlist. Links keep react-markdown's safe default.
  return node.tagName === "img" ? url : defaultUrlTransform(url);
}

export function MarkdownDocument({ text, headings, remoteImageBlocked }: { text: string; headings: MarkdownHeading[]; remoteImageBlocked: string }) {
  const components = useMemo<Components>(() => {
    const headingsByLine = new Map(headings.map((heading) => [heading.line, heading.id]));
    return {
      h1: headingComponent("h1", headingsByLine),
      h2: headingComponent("h2", headingsByLine),
      h3: headingComponent("h3", headingsByLine),
      h4: headingComponent("h4", headingsByLine),
      h5: headingComponent("h5", headingsByLine),
      h6: headingComponent("h6", headingsByLine),
      a({ node: _node, href, children, ...props }) {
        if (!href) return <>{children}</>;
        const anchor = href.startsWith("#");
        return <a href={href} target={anchor ? undefined : "_blank"} rel={anchor ? undefined : "noopener noreferrer"} {...props}>{children}</a>;
      },
      img({ node: _node, src, alt, ...props }) {
        if (!src) return null;
        if (!safeMarkdownImageSource(src)) {
          return <span className="markdown-image-blocked" role="note">{alt ? `${alt} — ${remoteImageBlocked}` : remoteImageBlocked}</span>;
        }
        return <img src={src} alt={alt ?? ""} loading="lazy" decoding="async" referrerPolicy="no-referrer" {...props} />;
      },
      table({ node: _node, children, ...props }) {
        return <div className="markdown-table-wrap"><table {...props}>{children}</table></div>;
      }
    };
  }, [headings, remoteImageBlocked]);

  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={filePreviewUrlTransform}>{text}</ReactMarkdown>;
}

function Outline({ headings, label }: { headings: MarkdownHeading[]; label: string }) {
  return (
    <nav className="file-preview-outline-list" aria-label={label}>
      {headings.map((heading) => (
        <a key={`${heading.line}-${heading.id}`} href={`#${heading.id}`} style={{ "--outline-depth": heading.depth } as React.CSSProperties}>
          {heading.text}
        </a>
      ))}
    </nav>
  );
}

export function GeneratedFilePreview({ file, fetchFile, downloadFile, labels, onClose }: Props) {
  const [state, setState] = useState<PreviewState>({ status: "idle" });
  const [retryKey, setRetryKey] = useState(0);
  const [downloadState, setDownloadState] = useState<"idle" | "loading" | "error">("idle");
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const kind = file ? filePreviewKind(file) : "unsupported";
  const imageLabelsRef = useRef({ tooLarge: labels.imageTooLarge, unavailable: labels.imageUnavailable });
  imageLabelsRef.current = { tooLarge: labels.imageTooLarge, unavailable: labels.imageUnavailable };

  useEffect(() => setDownloadState("idle"), [file?.path]);

  const handleDownload = async () => {
    if (!file || downloadState === "loading") return;
    setDownloadState("loading");
    try {
      await downloadFile(file);
      setDownloadState("idle");
    } catch {
      setDownloadState("error");
    }
  };

  useEffect(() => {
    if (!file) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);
    let iframeFocusTimer: number | null = null;
    const redirectIframeFocus = () => {
      iframeFocusTimer = window.setTimeout(() => {
        const active = document.activeElement;
        if (active instanceof HTMLIFrameElement && dialogRef.current?.contains(active)) {
          closeRef.current?.focus();
        }
      }, 0);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), summary, video[controls], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        const style = window.getComputedStyle(element);
        return style.display !== "none" && style.visibility !== "hidden" && element.getClientRects().length > 0;
      });
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", redirectIframeFocus);
    return () => {
      window.clearTimeout(focusTimer);
      if (iframeFocusTimer !== null) window.clearTimeout(iframeFocusTimer);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", redirectIframeFocus);
      document.body.style.overflow = previousOverflow;
      openerRef.current?.focus();
    };
  }, [file, onClose]);

  useEffect(() => {
    if (!file) {
      setState({ status: "idle" });
      return;
    }
    if (kind === "unsupported") {
      setState({ status: "unsupported" });
      return;
    }
    if (!previewSizeAllowed(kind, file.bytes)) {
      setState({ status: "skipped", message: labels.tooLarge });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ status: "loading" });
    fetchFile(file)
      .then(async (blob) => {
        if (cancelled) return;
        if (!previewSizeAllowed(kind, blob.size)) {
          setState({ status: "skipped", message: labels.tooLarge });
          return;
        }
        if (kind === "markdown") {
          const text = stripCitationMarkers(await blob.text());
          if (!cancelled) setState({ status: "markdown", text });
          return;
        }
        if (kind === "html") {
          const text = stripCitationMarkers(await blob.text());
          if (cancelled) return;
          // The static fallback is only needed if the isolated preview is unavailable; sanitising a large
          // (up to 20 MB) report would block the UI, so big files fall back to the "too large" note instead.
          const sanitized = text.length <= STATIC_HTML_FALLBACK_LIMIT
            ? DOMPurify.sanitize(text, { FORBID_TAGS: HTML_FORBID_TAGS, FORBID_ATTR: HTML_FORBID_ATTR })
            : `<p>${DOMPurify.sanitize(labels.tooLarge)}</p>`;
          // The isolated renderer cannot reach our API, so local images (chart.png next to report.html) are inlined.
          const raw = await inlineLocalImages(
            text,
            file.path,
            (path) => fetchFile({ path, name: path.split("/").pop() ?? path, bytes: 0, updatedAt: "" }),
            // Via a ref: callers pass fresh label functions each render, which must not re-trigger the load.
            { tooLarge: (name) => imageLabelsRef.current.tooLarge(name), unavailable: (name) => imageLabelsRef.current.unavailable(name) }
          );
          if (cancelled) return;
          setState({ status: "html", document: buildSandboxedHtmlDocument(sanitized), raw });
          return;
        }
        if (kind === "pdf") {
          const data = await blob.arrayBuffer();
          if (!cancelled) setState({ status: "pdf", data });
          return;
        }
        const mediaBlob = kind === "video" && !blob.type.startsWith("video/")
          ? blob.slice(0, blob.size, videoMimeType(file))
          : blob;
        objectUrl = URL.createObjectURL(mediaBlob);
        setState({ status: kind === "video" ? "video" : "image", url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", message: labels.unavailable });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fetchFile, file, kind, labels.tooLarge, labels.unavailable, retryKey]);

  const headings = useMemo(
    () => state.status === "markdown" ? extractMarkdownHeadings(state.text) : [],
    [state]
  );

  if (!file) return null;

  return (
    <div className="file-preview-root">
      <button type="button" className="file-preview-scrim" aria-label={labels.close} onClick={onClose} />
      <section ref={dialogRef} className={`file-preview-dialog is-${kind}`} role="dialog" aria-modal="true" aria-labelledby="file-preview-title">
        <header className="file-preview-header">
          <div className="file-preview-heading">
            <strong id="file-preview-title">{file.name}</strong>
            <span>{labels.kind[kind]}</span>
          </div>
          <div className="file-preview-actions">
            <button type="button" disabled={downloadState === "loading"} onClick={() => void handleDownload()}>
              {downloadState === "loading" ? labels.loading : labels.download}
            </button>
            <button ref={closeRef} type="button" className="file-preview-close" aria-label={labels.close} title={labels.close} onClick={onClose}>×</button>
          </div>
        </header>

        <div className="file-preview-body">
          {downloadState === "error" ? (
            <div className="file-preview-status" role="alert">
              <p>{labels.unavailable}</p>
              <button type="button" onClick={() => void handleDownload()}>{labels.retry}</button>
            </div>
          ) : null}
          {state.status === "loading" ? <div className="file-preview-status">{labels.loading}</div> : null}
          {state.status === "unsupported" ? <div className="file-preview-status">{labels.unsupported}</div> : null}
          {state.status === "skipped" ? <div className="file-preview-status">{state.message}</div> : null}
          {state.status === "error" ? (
            <div className="file-preview-status">
              <p>{state.message}</p>
              <button type="button" onClick={() => setRetryKey((value) => value + 1)}>{labels.retry}</button>
            </div>
          ) : null}
          {state.status === "markdown" ? (
            <div className="file-preview-markdown-layout">
              {headings.length > 0 ? (
                <>
                  <aside className="file-preview-outline">
                    <span>{labels.outline}</span>
                    <Outline headings={headings} label={labels.outline} />
                  </aside>
                  <details className="file-preview-outline-mobile">
                    <summary>{labels.outline}</summary>
                    <Outline headings={headings} label={labels.outline} />
                  </details>
                </>
              ) : null}
              <article className="file-preview-paper">
                <MarkdownDocument text={state.text} headings={headings} remoteImageBlocked={labels.remoteImageBlocked} />
              </article>
            </div>
          ) : null}
          {state.status === "image" ? (
            <div className="file-preview-media">
              <img
                src={state.url}
                alt={file.name}
                onError={() => {
                  URL.revokeObjectURL(state.url);
                  setState({ status: "error", message: labels.unavailable });
                }}
              />
            </div>
          ) : null}
          {state.status === "video" ? (
            <div className="file-preview-media file-preview-video">
              <video
                src={state.url}
                controls
                playsInline
                preload="metadata"
                onError={() => {
                  URL.revokeObjectURL(state.url);
                  setState({ status: "error", message: labels.unavailable });
                }}
              />
            </div>
          ) : null}
          {state.status === "pdf" ? (
            <PdfPreview
              data={state.data}
              title={file.name}
              labels={{
                loading: labels.loading,
                unavailable: labels.unavailable,
                page: labels.pdfPage,
                prev: labels.pdfPrev,
                next: labels.pdfNext
              }}
            />
          ) : null}
          {state.status === "html" ? (
            <HtmlPreviewFrame
              html={state.raw}
              fallbackDocument={state.document}
              title={file.name}
              labels={{ live: labels.htmlLive, fallback: labels.htmlSafety }}
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}
